import { useEffect, useRef, useState } from 'react';
import './portfolio.css';
import { useScrollReveal } from './useScrollReveal';
import { StorageConsentBanner, StorageSettingsLink, browserLocale } from '../components/StorageConsent';

const locale = browserLocale();

export function App() {
  const [scrolled, setScrolled] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const navToolsRef = useRef<HTMLLIElement>(null);
  const revealRef = useScrollReveal();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!toolsOpen) return;
    const close = () => setToolsOpen(false);
    const onKeydown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setToolsOpen(false);
    };
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKeydown);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKeydown);
    };
  }, [toolsOpen]);

  return (
    <>
      <nav id="main-nav" className={scrolled ? 'scrolled' : ''}>
        <a className="nav-logo" href="/">
          S.A.
        </a>
        <ul className="nav-links">
          <li>
            <a href="#about">About</a>
          </li>
          <li className={'nav-tools' + (toolsOpen ? ' open' : '')} ref={navToolsRef}>
            <button
              className="nav-tools-btn"
              aria-haspopup="true"
              aria-expanded={toolsOpen}
              aria-controls="tools-menu"
              onClick={(e) => {
                e.stopPropagation();
                setToolsOpen((v) => !v);
              }}
            >
              Tools <span className="nav-tools-caret">▾</span>
            </button>
            <div className="tools-menu" id="tools-menu" role="menu">
              <a className="tools-menu-item" href="/calculator.html" role="menuitem">
                <span className="tools-item-name">Financial Calculator</span>
                <span className="tools-item-desc">Compound interest &amp; rental tax (DE)</span>
              </a>
            </div>
          </li>
          <li>
            <a href="#contact">Contact</a>
          </li>
        </ul>
      </nav>

      <section id="hero">
        <div className="hero-left">
          <h1 className="hero-name">
            Saeed
            <br />
            <em>Ahmed</em>
          </h1>
          <div className="hero-divider"></div>
          <p className="hero-subtitle">Software Engineer</p>
        </div>

        <div className="hero-right">
          <ul className="interests-list">
            <li>Software Engineering</li>
            <li>Travel &amp; Exploration</li>
            <li>Finance &amp; Markets</li>
            <li>Motorcycle Riding</li>
            <li>Photography</li>
          </ul>
          <blockquote className="hero-quote">
            "Building systems by day,
            <br />
            exploring the world by weekend."
          </blockquote>
        </div>
      </section>

      <section id="about">
        <div className="section-inner reveal" ref={revealRef}>
          <div className="section-label">
            <span className="section-number">01</span>
            <span className="section-title">About</span>
          </div>
          <div>
            <p className="about-text">
              I'm a software engineer who believes the best ideas come at the intersection of code and curiosity.
              When I'm not engineering reliable systems or staying sharp on financial markets, I'm out on my
              motorcycle hunting for roads less traveled — or framing the world through a lens.
            </p>
            <div className="pills">
              <span className="pill">Problem solver</span>
              <span className="pill">Finance-curious</span>
              <span className="pill">Road wanderer</span>
              <span className="pill">Visual storyteller</span>
              <span className="pill">Lifelong learner</span>
            </div>
          </div>
        </div>
      </section>

      <section id="contact">
        <div className="section-inner reveal" ref={revealRef}>
          <div className="section-label">
            <span className="section-number">02</span>
            <span className="section-title">Contact</span>
          </div>
          <div className="contact-rows">
            <a className="contact-row" href="mailto:hello@auroori.de">
              <span className="platform">Email</span>
              <span className="contact-value">hello@auroori.de</span>
              <span className="arrow">→</span>
            </a>
            <a className="contact-row" href="https://linkedin.com/in/saeed05" target="_blank" rel="noopener">
              <span className="platform">LinkedIn</span>
              <span className="contact-value">linkedin.com/in/saeed05</span>
              <span className="arrow">→</span>
            </a>
            <a className="contact-row" href="https://instagram.com/imsas.17" target="_blank" rel="noopener">
              <span className="platform">Instagram</span>
              <span className="contact-value">@imsas.17</span>
              <span className="arrow">→</span>
            </a>
            <a className="contact-row" href="https://youtube.com/@shanto05" target="_blank" rel="noopener">
              <span className="platform">YouTube</span>
              <span className="contact-value">@shanto05</span>
              <span className="arrow">→</span>
            </a>
          </div>
        </div>
      </section>

      <footer>
        <span>© 2025 Saeed Ahmed</span>
        <em>"Building systems by day, exploring the world by weekend."</em>
        <StorageSettingsLink locale={locale} />
      </footer>

      <StorageConsentBanner locale={locale} />
    </>
  );
}
